import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { faCreditCard } from "@fortawesome/pro-solid-svg-icons";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import { SVG } from "../ui/SVG";
import Modal from "../ui/Modal";
import { toast } from "~/components/toast";
import { useAccount } from "~/hooks/useAccount";
import { skipTrialDiscount } from "~/api/billing/requests";
import { PlanStatus } from "~/types/account";
import { paths } from "~/router/paths";
import { Portal } from "~/helpers/portal";
import * as S from "./styles";

const CLOSE_DELAY_MS = 60_000;

export const SkipTrialBanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { account, getAccount } = useAccount();

  const [dismissed, setDismissed] = useState(false);
  const [showCloseBtn, setShowCloseBtn] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isOnTrial =
    account?.active_subscription?.status === PlanStatus.ACTIVE &&
    account?.active_subscription?.trial_end_at &&
    new Date(account.active_subscription.trial_end_at) > new Date();

  const shouldShow =
    isOnTrial &&
    !account?.has_redeemed_skip_trial_coupon &&
    (account?.payment_provider === "stripe" ||
      account?.active_subscription?.payment_provider === "stripe") &&
    !dismissed;

  // Show close button after 60 s delay (only when banner becomes visible)
  useEffect(() => {
    if (!shouldShow) return;

    setShowCloseBtn(false);
    const timer = setTimeout(() => setShowCloseBtn(true), CLOSE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [shouldShow]);
  const handleConfirm = useCallback(async () => {
    setIsLoading(true);
    try {
      await skipTrialDiscount();
      await getAccount();
      setShowModal(false);
      toast.success(String(t("skip_trial.success_description")), {
        title: String(t("skip_trial.success_title")),
      });
    } catch {
      toast.error(String(t("skip_trial.error")));
    } finally {
      setIsLoading(false);
    }
  }, [getAccount, t]);

  if (!shouldShow) {
    return null;
  }

  const cardDigits = account?.stripe_card_digits;

  return (
    <>
      {/* -------- Sticky Banner -------- */}
      <Portal>
        <S.BannerWrapper>
          <S.BannerCard>
            <S.CloseButton
              $visible={showCloseBtn}
              onClick={() => setDismissed(true)}
              aria-label="Close">
              <SVG icon={faXmark} size="lg" />
            </S.CloseButton>

            <S.BannerTitle>
              {t("skip_trial.banner_title")}
            </S.BannerTitle>
            <S.BannerDescription>
              {t("skip_trial.banner_description")}
            </S.BannerDescription>

            <S.ClaimButton onClick={() => setShowModal(true)}>
              {t("skip_trial.claim_discount")}
            </S.ClaimButton>
          </S.BannerCard>
        </S.BannerWrapper>
      </Portal>

      {/* -------- Confirm Modal -------- */}
      <Modal
        isShowing={showModal}
        hide={() => setShowModal(false)}
        maxWidth={480}
        padding="2.4rem">
        <S.ModalBody>
          <S.ModalTitle>{t("skip_trial.confirm_title")}</S.ModalTitle>
          <S.ModalDescription>
            {t("skip_trial.confirm_description")}
          </S.ModalDescription>

          <S.TestimonialRow>
            {t("skip_trial.confirm_testimonial")}
          </S.TestimonialRow>

          {cardDigits && (
            <S.CardRow>
              <S.CardInfo>
                <SVG icon={faCreditCard} size="lg" />
                <div>
                  <S.CardLabel>
                    {t("skip_trial.credit_debit_card")}
                  </S.CardLabel>
                  <br />
                  <S.CardDigits>**** **** **** {cardDigits}</S.CardDigits>
                </div>
              </S.CardInfo>
              <S.ChangeCardLink
                onClick={() => {
                  setShowModal(false);
                  navigate(paths.settings.account);
                }}>
                {t("skip_trial.change_card")}
              </S.ChangeCardLink>
            </S.CardRow>
          )}

          <S.ConfirmButton
            onClick={handleConfirm}
            disabled={isLoading}
            $loading={isLoading}>
            {isLoading
              ? t("skip_trial.loading")
              : t("skip_trial.confirm_button")}
          </S.ConfirmButton>
        </S.ModalBody>
      </Modal>
    </>
  );
};
